import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { TOOLKIT } from "@/data/content";
import BreathingExercise from "@/components/BreathingExercise";
import ScriptureCard from "@/components/ScriptureCard";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import {
  Wind, Timer, BookOpenText, Footprints, GlassWater, PhoneCall, PenLine, Hand, X,
} from "lucide-react";

const ICONS = { Wind, Timer, BookOpenText, Footprints, GlassWater, PhoneCall, PenLine, Hand };

function UrgeTimer({ onDone }) {
  const [seconds, setSeconds] = useState(600);
  const [running, setRunning] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(ref.current);
          onDone?.();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running, onDone]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const pct = ((600 - seconds) / 600) * 100;

  return (
    <div className="text-center py-6" data-testid="urge-timer">
      <p className="text-olive/70 mb-6 max-w-sm mx-auto">
        Cravings rise like a wave and fall within about ten minutes. You don't have to fight it — just outlast it. Breathe. Pray. Wait.
      </p>
      <div className="relative w-52 h-52 mx-auto mb-6">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#E8EFEA" strokeWidth="6" />
          <circle cx="50" cy="50" r="45" fill="none" stroke="#7C9B9F" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 45}`} strokeDashoffset={`${2 * Math.PI * 45 * (1 - pct / 100)}`}
            style={{ transition: "stroke-dashoffset 1s linear" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-serif text-5xl text-olive">{mm}:{ss}</span>
          <span className="text-xs text-stone mt-1">{seconds === 0 ? "You made it." : "riding the wave"}</span>
        </div>
      </div>
      {seconds === 0 ? (
        <p className="font-serif text-2xl text-gold">The craving passed. So will the next.</p>
      ) : (
        <Button data-testid="urge-pause" variant="outline" onClick={() => setRunning((r) => !r)} className="rounded-full border-olive/20 hover:bg-sage">
          {running ? "Pause" : "Resume"}
        </Button>
      )}
    </div>
  );
}

export default function ToolkitPage() {
  const [active, setActive] = useState(null);

  const logCoping = async (tool) => {
    try {
      await client.post("/cravings", { intensity: 3, coped_with: tool, note: "Used the 72h toolkit" });
    } catch (e) {}
  };

  const open = (tool) => {
    setActive(tool);
    if (["breathe", "urge", "prayer"].includes(tool.type)) logCoping(tool.title);
  };

  return (
    <div className="space-y-8" data-testid="toolkit-page">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs text-terracotta mb-2">For any craving, any day</p>
        <h1 className="font-serif text-display text-olive">Your toolkit</h1>
        <p className="text-olive/70 mt-3 max-w-2xl">
          Whenever an urge comes — today, next week, or a year from now — don't face it alone. Reach for one of these.
          Delay, distract, breathe, and pray. It will pass.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TOOLKIT.map((t, i) => {
          const Icon = ICONS[t.icon];
          return (
            <motion.button
              key={t.id}
              data-testid={`tool-${t.id}`}
              onClick={() => open(t)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="text-left rounded-3xl bg-card border border-border p-6 shadow-soft transition-colors duration-200 hover:bg-sage"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="rounded-2xl bg-sage w-11 h-11 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-sky" />
                </div>
                <span className="text-[11px] uppercase tracking-[0.15em] text-stone">{t.tag}</span>
              </div>
              <h3 className="font-serif text-2xl text-olive mb-1.5">{t.title}</h3>
              <p className="text-sm text-olive/70 leading-relaxed">{t.desc}</p>
            </motion.button>
          );
        })}
      </div>

      <ResponsiveModal open={!!active} onOpenChange={(o) => !o && setActive(null)} testid="tool-dialog"
        title={active?.title} description={active?.desc}>
        {active && (
          <div className="pb-2">
            {active.type === "breathe" && <BreathingExercise />}
            {active.type === "urge" && <UrgeTimer onDone={() => toast.success("You rode it out. That's real strength.")} />}
            {active.type === "prayer" && (
              <div className="space-y-4 py-2">
                <ScriptureCard compact />
                <p className="text-olive/75 text-center italic font-scripture text-scripture">
                  “Lord, this moment is heavier than I am. Be my strength where I have none. Amen.”
                </p>
              </div>
            )}
            {active.type === "info" && (
              <div className="rounded-2xl bg-sage p-6 text-olive/80 leading-relaxed">
                {active.desc} Take it slowly — you only have to get through this one moment, then the next.
              </div>
            )}
          </div>
        )}
      </ResponsiveModal>
    </div>
  );
}
